import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

import { usePartnersDeleteEvent, usePartnersEventAction, usePartnersReadEvent } from '@/api/hooks';
import { type Partner } from '@/api/routes/partners/partners.api';
import LoadErrorState from '@/components/LoadErrorState';
import EventConfirmModal from '@/domain/events/components/EventConfirmModal';
import EventDetailHeader from '@/domain/events/components/EventDetailHeader';
import EventDetailIndicators from '@/domain/events/components/EventDetailIndicators';
import EventDetailInfo from '@/domain/events/components/EventDetailInfo';
import EventDetailSkeleton from '@/domain/events/components/EventDetailSkeleton';
import EventReviewHistory from '@/domain/events/components/EventReviewHistory';
import EventStatusAlert from '@/domain/events/components/EventStatusAlert';
import EventSubmitChecklist from '@/domain/events/components/EventSubmitChecklist';
import { getPartnerEventActions } from '@/domain/events/utils';
import { useTranslate } from '@/state/i18n';
import { ScreenRoutes } from '@/types/router';

interface Props {
  partner: Partner;
}

type PendingConfirmation = 'cancel' | 'delete' | null;

/** `/events/:eventId` (P10–P13): the event as the user will see it, its status and next step. */
const EventDetail = ({ partner }: Props) => {
  const t = useTranslate('domain.events.EventDetail');
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { eventId = '' } = useParams<{ eventId: string }>();
  const { data: event, isLoading, isError, refetch } = usePartnersReadEvent(eventId);
  const { mutateAsync: submit, isPending: isSubmitting } = usePartnersEventAction('submit');
  const { mutateAsync: cancel, isPending: isCancelling } = usePartnersEventAction('cancel');
  const { mutateAsync: remove, isPending: isDeleting } = usePartnersDeleteEvent();
  const [confirmation, setConfirmation] = useState<PendingConfirmation>(null);

  if (isLoading) return <EventDetailSkeleton />;
  if (isError || !event) {
    return <LoadErrorState message={t('loadError')} onRetry={() => refetch()} />;
  }

  const actions = getPartnerEventActions(event, partner.status);
  const showChecklist = actions.submit || actions.submitBlockedBy !== null;
  const showIndicators = event.reviewStatus !== 'draft' || Boolean(event.canceledAt);
  const goToEdit = () => navigate(generatePath(ScreenRoutes.EVENT_EDIT, { eventId }));

  // Errors (409, 422) are shown by the global snackbar with the API message.
  const handleSubmit = async () => {
    try {
      await submit(eventId);
      enqueueSnackbar(t('submitted'), { variant: 'success' });
    } catch {
      // Already shown.
    }
  };
  const handleCancel = async () => {
    try {
      await cancel(eventId);
      enqueueSnackbar(t('canceled'), { variant: 'success' });
    } catch {
      // Already shown.
    } finally {
      setConfirmation(null);
    }
  };
  const handleDelete = async () => {
    try {
      await remove(eventId);
      enqueueSnackbar(t('deleted'), { variant: 'success' });
      navigate(ScreenRoutes.EVENTS, { replace: true });
    } catch {
      setConfirmation(null);
    }
  };

  return (
    <Stack gap={3}>
      <EventDetailHeader
        event={event}
        actions={actions}
        submitting={isSubmitting}
        onBack={() => navigate(ScreenRoutes.EVENTS)}
        onEdit={goToEdit}
        onSubmit={handleSubmit}
        onCancel={() => setConfirmation('cancel')}
        onDelete={() => setConfirmation('delete')}
      />
      <EventStatusAlert event={event} />
      <Box sx={sx.columns}>
        <EventDetailInfo event={event} />
        <Stack gap={3}>
          {showChecklist ? (
            <EventSubmitChecklist
              event={event}
              partnerStatus={partner.status}
              onReviewAddress={goToEdit}
            />
          ) : null}
          {showIndicators ? <EventDetailIndicators event={event} /> : null}
          <EventReviewHistory reviews={event.reviews} />
        </Stack>
      </Box>

      <EventConfirmModal
        open={confirmation === 'cancel'}
        title={t('cancelModal.title')}
        message={t('cancelModal.message', { count: event.upcomingIntentsCount })}
        confirmLabel={t('cancelModal.confirm')}
        cancelLabel={t('cancelModal.back')}
        destructive
        loading={isCancelling}
        onConfirm={handleCancel}
        onClose={() => setConfirmation(null)}
      />
      <EventConfirmModal
        open={confirmation === 'delete'}
        title={t('deleteModal.title')}
        message={t('deleteModal.message')}
        confirmLabel={t('deleteModal.confirm')}
        cancelLabel={t('deleteModal.back')}
        destructive
        loading={isDeleting}
        onConfirm={handleDelete}
        onClose={() => setConfirmation(null)}
      />
    </Stack>
  );
};

export default EventDetail;

const sx = {
  columns: {
    display: 'grid',
    gap: 3,
    alignItems: 'start',
    gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 2fr) minmax(0, 1fr)' },
  },
};
